import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { boatsService } from '../services/boats/boatsService';
import type {
  Boat,
  CreateBoatPayload,
  UpdateBoatPayload,
  AssignBoatSeatPayload,
} from '../types/boats';

/** Peach Rig Info rows for a boat (port/starboard per seat). */
export function useBoatSeatRigging(boatId: string | undefined) {
  return useQuery({
    queryKey: ['boat-seat-rigging', boatId],
    queryFn: async () => {
      if (!boatId) return [];
      const { data, error } = await boatsService.listBoatSeatRigging(boatId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!boatId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to get all boats for a team
 * @param teamId Team ID
 * @param enabled Whether the query should run
 */
export function useTeamBoats(teamId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['team-boats', teamId],
    queryFn: async (): Promise<Boat[]> => {
      if (!teamId) return [];
      const { data, error } = await boatsService.getTeamBoats(teamId);
      if (error) throw error;
      return data || [];
    },
    enabled: enabled && !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to create a boat
 */
export function useCreateBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBoatPayload) => {
      const { data, error } = await boatsService.createBoat(payload);
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate team boats query
      queryClient.invalidateQueries({ queryKey: ['team-boats', variables.team_id] });
    },
  });
}

/**
 * Hook to update a boat
 */
export function useUpdateBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boatId, payload }: { boatId: string; payload: UpdateBoatPayload }) => {
      const { data, error } = await boatsService.updateBoat(boatId, payload);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        // Invalidate team boats query
        queryClient.invalidateQueries({ queryKey: ['team-boats', data.team_id] });
        // Invalidate individual boat query
        queryClient.invalidateQueries({ queryKey: ['boat', data.id] });
      }
    },
  });
}

/**
 * Hook to delete a boat
 */
export function useDeleteBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boatId, teamId }: { boatId: string; teamId: string }) => {
      const { error } = await boatsService.deleteBoat(boatId);
      if (error) throw error;
      return { boatId, teamId };
    },
    onSuccess: (result) => {
      // Invalidate team boats query
      queryClient.invalidateQueries({ queryKey: ['team-boats', result.teamId] });
    },
  });
}

/**
 * Hook to assign a rower to a boat seat
 */
export function useAssignBoatSeat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AssignBoatSeatPayload) => {
      const { data, error } = await boatsService.assignBoatSeat(payload);
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate boat roster query
      queryClient.invalidateQueries({ queryKey: ['boat-roster', variables.boat_id] });
      queryClient.invalidateQueries({ queryKey: ['boat', variables.boat_id] });
    },
  });
}

/**
 * Hook to unassign a rower from a boat seat
 */
export function useUnassignBoatSeat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boatId, position }: { boatId: string; position: number }) => {
      const { error } = await boatsService.unassignBoatSeat(boatId, position);
      if (error) throw error;
      return { boatId, position };
    },
    onSuccess: (result) => {
      // Invalidate boat roster query
      queryClient.invalidateQueries({ queryKey: ['boat-roster', result.boatId] });
      queryClient.invalidateQueries({ queryKey: ['boat', result.boatId] });
    },
  });
}

/**
 * Hook to clear all assignments for a boat
 */
export function useClearBoatRoster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (boatId: string) => {
      const { error } = await boatsService.clearBoatRoster(boatId);
      if (error) throw error;
      return boatId;
    },
    onSuccess: (boatId) => {
      // Invalidate boat roster query
      queryClient.invalidateQueries({ queryKey: ['boat-roster', boatId] });
      queryClient.invalidateQueries({ queryKey: ['boat', boatId] });
    },
  });
}
